import { useEffect, useState, useCallback, useRef } from "react";
import { ChevronUp, ChevronDown, Plus, Copy } from "lucide-react";
import { api, formatApiErrorDetail, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/pages/admin/ImageUploader";
import LangToggle from "@/pages/admin/LangToggle";
import { useAdminSave } from "@/lib/adminSaveContext";

const EMPTY = {
  slug: "",
  slug_en: "",
  slug_ro: "",
  category_ro: "",
  category_en: "",
  title_ro: "",
  title_en: "",
  excerpt_ro: "",
  excerpt_en: "",
  body_ro: "",
  body_en: "",
  cover_image: "",
  reading_time: "",
  seo_title_ro: "",
  seo_title_en: "",
  meta_description_ro: "",
  meta_description_en: "",
  order: 0,
  published: true,
};

const field =
  "w-full bg-transparent border border-ink/25 px-3 py-2 outline-none focus:border-ink transition-colors font-light text-sm";

export default function JournalManager() {
  const [essays, setEssays] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [lang, setLang] = useState("ro");
  const { registerSaver } = useAdminSave();

  const load = useCallback(async () => {
    const { data } = await api.get("/essays?include_unpublished=true");
    setEssays(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "order" ? Number(e.target.value) : e.target.value }));

  const langSet = (k) => (e) => setForm((f) => ({ ...f, [`${k}_${lang}`]: e.target.value }));
  const L = lang.toUpperCase();

  const persist = useCallback(async () => {
    if (editingId) {
      await api.put(`/essays/${editingId}`, form);
    } else if (JSON.stringify(form) !== JSON.stringify(EMPTY)) {
      await api.post("/essays", form);
    } else {
      return true;
    }
    setForm(EMPTY);
    setEditingId(null);
    await load();
    return true;
  }, [editingId, form, load]);

  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => {
    registerSaver(() => persistRef.current());
    return () => registerSaver(null);
  }, [registerSaver]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const creating = !editingId;
      await persist();
      toast.success(creating ? "Eseu creat" : "Eseu actualizat");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const edit = (es) => {
    setEditingId(es.id);
    setForm({ ...EMPTY, ...es });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Ștergi acest eseu?")) return;
    await api.delete(`/essays/${id}`);
    toast.success("Șters");
    if (editingId === id) {
      setForm(EMPTY);
      setEditingId(null);
    }
    load();
  };

  const startNew = () => {
    setForm(EMPTY);
    setEditingId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicate = (es) => {
    const clone = { ...EMPTY, ...es };
    delete clone.id;
    delete clone.created_at;
    if (clone.slug) clone.slug = `${clone.slug}-copy`;
    if (clone.slug_en) clone.slug_en = `${clone.slug_en}-copy`;
    if (clone.slug_ro) clone.slug_ro = `${clone.slug_ro}-copy`;
    if (clone.title_ro) clone.title_ro = `${clone.title_ro} (Copie)`;
    if (clone.title_en) clone.title_en = `${clone.title_en} (Copy)`;
    clone.published = false;
    setForm(clone);
    setEditingId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Șablon duplicat — revizuiește slug-ul și apasă Creează");
  };

  const move = async (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= essays.length) return;
    const a = essays[index];
    const b = essays[to];
    const aOrder = a.order ?? index;
    const bOrder = b.order ?? to;
    const same = aOrder === bOrder;
    try {
      await Promise.all([
        api.put(`/essays/${a.id}`, { ...a, order: same ? to : bOrder }),
        api.put(`/essays/${b.id}`, { ...b, order: same ? index : aOrder }),
      ]);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reordonare eșuată");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <div className="eyebrow mb-6">{editingId ? "Editează eseu" : "Eseu nou"}</div>
        <form onSubmit={save} className="space-y-4" data-testid="essay-form">
          <div className="flex items-center justify-between gap-4">
            <div className="eyebrow text-ink/50">Conținut în limba:</div>
            <LangToggle lang={lang} setLang={setLang} testidPrefix="essay-form" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input placeholder="slug (identificator)" value={form.slug} onChange={set("slug")} className={field} required data-testid="f-slug" />
            <input placeholder="Timp lectură (6 min)" value={form.reading_time} onChange={set("reading_time")} className={field} />
            <input type="number" placeholder="Ordine" value={form.order} onChange={set("order")} className={field} />
          </div>
          <input placeholder={`URL ${L} (ex: economia-increderii)`} value={form[`slug_${lang}`] || ""} onChange={langSet("slug")} className={field} data-testid={`f-slug-${lang}`} />
          <input placeholder={`Categorie (${L})`} value={form[`category_${lang}`] || ""} onChange={langSet("category")} className={field} data-testid={`f-category-${lang}`} />
          <input placeholder={`Titlu (${L})`} value={form[`title_${lang}`] || ""} onChange={langSet("title")} className={field} required data-testid={`f-title-${lang}`} />
          <textarea rows={2} placeholder={`Rezumat (${L})`} value={form[`excerpt_${lang}`] || ""} onChange={langSet("excerpt")} className={field} data-testid={`f-excerpt-${lang}`} />
          <textarea rows={8} placeholder={`Text (${L})`} value={form[`body_${lang}`] || ""} onChange={langSet("body")} className={field} data-testid={`f-body-${lang}`} />
          <div>
            <div className="eyebrow text-ink/50 mb-2">Imagine copertă</div>
            <ImageUploader value={form.cover_image} onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))} testid="essay-cover" />
          </div>
          <div className="border-t border-ink/15 pt-4 space-y-3">
            <div className="eyebrow text-ink/50">SEO ({L}) — suprascrie titlul și descrierea din &lt;head&gt; + preview social</div>
            <input placeholder={`SEO Title (${L})`} value={form[`seo_title_${lang}`] || ""} onChange={langSet("seo_title")} className={field} data-testid={`f-seo-title-${lang}`} />
            <textarea rows={2} placeholder={`Meta Description (${L})`} value={form[`meta_description_${lang}`] || ""} onChange={langSet("meta_description")} className={field} data-testid={`f-meta-desc-${lang}`} />
          </div>
          <label className="flex items-center gap-3 eyebrow">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Publicat
          </label>
          <div className="flex gap-3">
            <button type="submit" data-testid="essay-save" className="eyebrow border border-ink px-6 py-3 hover:bg-ink hover:text-paper transition-colors">
              {editingId ? "Salvează" : "Creează"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setForm(EMPTY);
                  setEditingId(null);
                }}
                className="eyebrow px-6 py-3 hover:text-ink"
              >
                Anulează
              </button>
            )}
          </div>
        </form>
      </div>

      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="eyebrow">Eseuri ({essays.length})</div>
          <button type="button" onClick={startNew} data-testid="essay-add-new" className="eyebrow inline-flex items-center gap-1.5 border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors">
            <Plus size={13} /> Adaugă eseu
          </button>
        </div>
        <div className="border border-line">
          {essays.map((es, index) => (
            <div key={es.id} className="flex items-center justify-between gap-4 p-4 border-b border-line last:border-0" data-testid={`row-${es.slug}`}>
              <div className="min-w-0 flex items-center gap-3">
                <div className="flex flex-col shrink-0">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} data-testid={`essay-up-${es.slug}`} className="p-0.5 border border-ink/20 hover:bg-ink hover:text-paper transition-colors disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-ink"><ChevronUp size={13} /></button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === essays.length - 1} data-testid={`essay-down-${es.slug}`} className="p-0.5 border border-ink/20 border-t-0 hover:bg-ink hover:text-paper transition-colors disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-ink"><ChevronDown size={13} /></button>
                </div>
                {es.cover_image && <img src={assetUrl(es.cover_image)} alt="" className="w-12 h-12 object-cover shrink-0" />}
                <div className="min-w-0">
                  <div className="font-display text-xl truncate">{es.title_ro}</div>
                  <div className="eyebrow mt-1 flex gap-3">
                    <span className="tnum">#{es.order}</span>
                    <span>{es.slug}</span>
                    {!es.published && <span className="text-red-700">draft</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => edit(es)} data-testid={`edit-${es.slug}`} className="eyebrow border border-ink px-3 py-2 hover:bg-ink hover:text-paper transition-colors">
                  Editează
                </button>
                <button onClick={() => duplicate(es)} title="Duplică" data-testid={`duplicate-${es.slug}`} className="eyebrow border border-ink px-3 py-2 hover:bg-ink hover:text-paper transition-colors inline-flex items-center gap-1">
                  <Copy size={12} /> Duplică
                </button>
                <button onClick={() => del(es.id)} data-testid={`del-${es.slug}`} className="eyebrow border border-red-700 text-red-700 px-3 py-2 hover:bg-red-700 hover:text-paper transition-colors">
                  Șterge
                </button>
              </div>
            </div>
          ))}
          {essays.length === 0 && <div className="p-6 eyebrow">Niciun eseu.</div>}
        </div>
      </div>
    </div>
  );
}
